/**
 * The launch (Stage 4, Sam 2026-10-20: "glow hand-off", built petal by petal). On the app's own background the petals
 * load clockwise; while the app is not ready they chase round; when it is, the centre dot lands with a little glow and
 * the mark flies into its place in the header (top left), the background fading as it goes.
 *
 * Ready = signed-in state known (authReady) and the first screen painted; ?ready=<ms> in a dev build holds it longer so
 * the waiting loop can be seen (/lab/motion). Once per app run.
 */
import { useEffect, useRef, useState } from 'react';
import PetalMark, { VARIANTS } from './PetalMark';
import { PriiWordmark } from '../PriiLogo';
import { useCurrentUserProfile } from '../../contexts/CurrentUserProfileContext';
import { motionFlags } from '../../lib/motionFlags';
import AmbientOrbField from '../AmbientOrbField';
import { appLog, quickReloadRoute } from '../../lib/appLog';
import { watchForBlank } from '../../lib/blankScreenWatch';

const SIZE = 120;
const NAME_IN = { duration: 600, delay: 100, easing: 'cubic-bezier(.33,0,.2,1)', fill: 'forwards' };
let shown = false;
let up = false;
const launchT = { ready: null, gone: null };
/** True while the launch splash covers the app: anything that starts moving "when the page appears" waits for it. */
export function isLaunchSplashUp() {
  return up;
}

export default function LaunchSplash() {
  const { launch: variant, ready: holdMs } = motionFlags();
  // ?launch=off: the lab's other frames. A quick reload (the web process replaced while you were away, 2026-10-29) is a
  // return to the page you were on, not a launch: no splash, the page comes straight back.
  const [on] = useState(
    () => Boolean(variant) && variant !== 'off' && !shown && !quickReloadRoute(),
  );
  const { authReady } = useCurrentUserProfile();
  const [held, setHeld] = useState(holdMs > 0);
  const [gone, setGoneState] = useState(false);
  // Shown once it has run to the end: the layout remounts during start-up, and a flag set on mount hid the splash then.
  const setGone = (v) => {
    if (v) {
      shown = true;
      launchT.gone = Math.round(performance.now());
      if (typeof window !== 'undefined') window.__launch = { ...launchT };
      appLog('info', 'perf.launch', { ...launchT, variant });
      watchForBlank('launch');
    }
    setGoneState(v);
  };
  const veilRef = useRef(null),
    markRef = useRef(null),
    wordRef = useRef(null),
    nameAnim = useRef(null);

  // No splash (a quick reload, or ?launch=off): the empty-screen check starts with the page
  useEffect(() => {
    if (!on) watchForBlank('reload');
  }, [on]);

  useEffect(() => {
    if (!holdMs) return undefined;
    const t = setTimeout(() => setHeld(false), holdMs);
    return () => clearTimeout(t);
  }, [holdMs]);
  const [painted, setPainted] = useState(false);
  // Two frames after mount (the first screen is on the page); a timer too, because a hidden page never runs frames.
  useEffect(() => {
    let a = requestAnimationFrame(() => {
      a = requestAnimationFrame(() => setPainted(true));
    });
    const t = setTimeout(() => setPainted(true), 250);
    return () => {
      cancelAnimationFrame(a);
      clearTimeout(t);
    };
  }, []);

  const nameEarly = Boolean(VARIANTS[variant]?.nameEarly);
  // The name fades in slowly, a soft ease with no snap at the end (Sam, 2026-10-23: it looked like it snapped in).
  useEffect(() => {
    // springName: the name is there from the start, under the petals as they pop in
    if (!on || !nameEarly || !wordRef.current) return;
    nameAnim.current = wordRef.current.animate(
      [
        { opacity: 0, transform: 'translateY(6px)' },
        { opacity: 1, transform: 'none' },
      ],
      NAME_IN,
    );
  }, [on, nameEarly]);

  useEffect(() => {
    up = on && !gone;
    return () => {
      up = false;
    };
  }, [on, gone]);

  const ready = authReady && painted && !held;
  // Launch timings (ms since the page started): in the app logs as perf.launch, so a slow launch on a phone shows where
  // the time went - waiting for the app, or the animation after it was ready.
  if (on && ready && launchT.ready == null) launchT.ready = Math.round(performance.now());
  if (!on || gone) return null;

  // Fully loaded: the name rises under the mark in the header's own wordmark (Sam, 2026-10-20), holds a beat, and fades
  // as the mark flies to the corner.
  const nameThenFly = () => {
    const word = wordRef.current;
    if (!word) {
      flyHome();
      return;
    }
    if (nameEarly) {
      const later = () => setTimeout(flyHome, 140);
      if (nameAnim.current) nameAnim.current.finished.then(later, later);
      else later();
      return;
    }
    word
      .animate(
        [
          { opacity: 0, transform: 'translateY(6px)' },
          { opacity: 1, transform: 'none' },
        ],
        { ...NAME_IN, delay: 0 },
      )
      .finished.then(() => setTimeout(flyHome, 280))
      .catch(flyHome);
  };
  const flyHome = () => {
    const veil = veilRef.current,
      mark = markRef.current;
    if (!veil || !mark) {
      setGone(true);
      return;
    }
    wordRef.current?.animate([{ opacity: 1 }, { opacity: 0, transform: 'translateY(-4px)' }], {
      duration: 220,
      easing: 'ease-in',
      fill: 'forwards',
    });
    // The header's own mark: whichever is on screen (the phone bar's, or the wide one's).
    const target = [...document.querySelectorAll('header .prii-mark-bare')].find(
      (el) => el.offsetParent !== null && el.getBoundingClientRect().width > 0,
    );
    const from = mark.getBoundingClientRect();
    veil.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 340,
      delay: 160,
      easing: 'ease-out',
      fill: 'forwards',
    });
    if (!target) {
      mark
        .animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(.6)' }], {
          duration: 400,
          fill: 'forwards',
        })
        .finished.then(() => setGone(true));
      return;
    }
    const to = target.getBoundingClientRect();
    target.style.visibility = 'hidden';
    const dx = to.left + to.width / 2 - (from.left + from.width / 2),
      dy = to.top + to.height / 2 - (from.top + from.height / 2),
      k = to.width / from.width;
    mark
      .animate([{ transform: 'none' }, { transform: `translate(${dx}px, ${dy}px) scale(${k})` }], {
        duration: 500,
        easing: 'cubic-bezier(.6,0,.2,1)',
        fill: 'forwards',
      })
      .finished.then(() => {
        target.style.visibility = '';
        setGone(true);
      })
      .catch(() => {
        target.style.visibility = '';
        setGone(true);
      });
  };

  return (
    <div className="launch-splash" aria-hidden="true">
      {/* The app's own orbs and sparkles under the mark (Sam, 2026-10-22), the same field as the page's (one cached layout), so
          the veil fading off leaves the same background behind. It unmounts with the splash. */}
      <div ref={veilRef} className="launch-splash__veil bg-gray-50 dark:bg-dark-bg">
        <div className="ambient-orb-layer">
          <AmbientOrbField />
        </div>
      </div>
      <div ref={markRef} className="launch-splash__mark">
        <PetalMark variant={variant} size={SIZE} ready={ready} onFinished={nameThenFly} />
      </div>
      <div ref={wordRef} className="launch-splash__name">
        <PriiWordmark size="sm" />
      </div>
    </div>
  );
}
